import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import ThemeToggle from './ThemeToggle';
import { useTheme } from '../context/ThemeContext';

const Header = () => {
  const { theme } = useTheme();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const productsTimeout = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    if (!isHomePage) {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const scrollItems = [
    { label: 'Home', id: 'hero' },
    { label: 'Solutions', id: 'solutions' },
    { label: 'Projects', id: 'projects' },
    { label: 'About', id: 'about' },
  ];

  const productLinks = [
    { label: 'Vulcan Series', path: '/vulcan' },
    { label: 'Astra Series', path: '/astra' },
  ];

  const openProducts = () => {
    clearTimeout(productsTimeout.current);
    setIsProductsOpen(true);
  };
  const closeProducts = () => {
    productsTimeout.current = setTimeout(() => setIsProductsOpen(false), 150);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-smooth ${
        isScrolled ? 'glass-frosted glow-subtle' : ''
      }`}
      style={{ padding: '1rem 2rem' }}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-3 group"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center">
              <img
                src={theme === 'dark' ? '/logo-dark.png' : '/logo-light.png'}
                alt="Vinayak Robotics Logo"
                className="w-full h-full object-contain rounded-lg transition-opacity duration-300"
              />
            </div>
            <div>
              <div className="font-bold text-xl tracking-tight text-foreground-primary">
                Vinayak Robotics
              </div>
            </div>
          </div>
        </Link>

        {/* Desktop Menu */}
        <nav className="hidden md:flex items-center gap-8">
          {scrollItems.slice(0, 2).map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className="text-sm font-medium transition-smooth hover:scale-105 text-foreground-secondary hover:text-accent"
            >
              {item.label}
            </button>
          ))}

          {/* Products Dropdown */}
          <div
            className="relative"
            onMouseEnter={openProducts}
            onMouseLeave={closeProducts}
          >
            <button
              onClick={() => scrollToSection('products')}
              className="text-sm font-medium transition-smooth hover:scale-105 text-foreground-secondary hover:text-accent flex items-center gap-1"
            >
              Products
              <ChevronDown
                size={14}
                className={`transition-transform ${isProductsOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {isProductsOpen && (
              <div className="absolute top-full left-0 mt-2 glass-frosted rounded-xl p-3 min-w-[180px] glow-subtle">
                {productLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="block py-2 px-3 text-sm rounded-lg text-foreground-secondary hover:text-accent transition-smooth"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {scrollItems.slice(2).map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className="text-sm font-medium transition-smooth hover:scale-105 text-foreground-secondary hover:text-accent"
            >
              {item.label}
            </button>
          ))}
        </nav>

        {/* Right Side: Theme Toggle + Contact Button */}
        <div className="hidden md:flex items-center gap-3">
          <ThemeToggle />
          <Button
            onClick={() => navigate('/contact')}
            className="font-semibold px-6 transition-smooth"
            style={{ background: '#F97316', color: '#FFFFFF' }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.05)';
              e.target.style.boxShadow = '0 0 30px rgba(249, 115, 22, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = 'none';
            }}
          >
            Contact
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center gap-2">
          <ThemeToggle />
          <button
            className="text-foreground-primary p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden mt-4 glass-frosted rounded-xl p-6 space-y-4">
          {scrollItems.slice(0, 2).map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className="block w-full text-left py-2 text-sm font-medium text-foreground-secondary"
            >
              {item.label}
            </button>
          ))}

          {/* Mobile Products Expand */}
          <div>
            <button
              onClick={() => setIsProductsOpen(!isProductsOpen)}
              className="flex items-center justify-between w-full text-left py-2 text-sm font-medium text-foreground-secondary"
            >
              Products
              <ChevronDown
                size={14}
                className={`transition-transform ${isProductsOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {isProductsOpen && (
              <div className="pl-4 space-y-2 mt-1">
                {productLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block py-1.5 text-sm text-foreground-secondary hover:text-accent"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {scrollItems.slice(2).map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className="block w-full text-left py-2 text-sm font-medium text-foreground-secondary"
            >
              {item.label}
            </button>
          ))}

          <Button
            onClick={() => {
              navigate('/contact');
              setIsMobileMenuOpen(false);
            }}
            className="w-full font-semibold"
            style={{ background: '#F97316', color: '#FFFFFF' }}
          >
            Contact
          </Button>
        </div>
      )}
    </header>
  );
};

export default Header;
